// Tableau de bord — vue d'accueil avec métriques agrégées en temps réel
// et mini-aperçu cliquable des états comptables.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Wallet, TrendingUp, TrendingDown, Percent, ArrowDownLeft, ArrowUpRight,
  Loader2, Scale, BookOpen, Layers, FileBarChart, Receipt, ChevronRight, Users,
} from "lucide-react";

const anneeCourante = new Date().getFullYear();
const fmt = (n) => Math.round(n || 0).toLocaleString("fr-FR");

const ETATS = [
  { titre: "Balance générale", desc: "Soldes de tous les comptes", icone: FileBarChart, chemin: "/etats/balance" },
  { titre: "Grand livre", desc: "Détail des écritures par compte", icone: BookOpen, chemin: "/etats/grand-livre" },
  { titre: "Journaux", desc: "Écritures par journal", icone: Layers, chemin: "/etats/journaux" },
  { titre: "Compte de résultat", desc: "Produits − charges", icone: TrendingUp, chemin: "/etats/compte-de-resultat" },
  { titre: "Bilan", desc: "Actif / passif", icone: Scale, chemin: "/etats/bilan" },
  { titre: "Déclaration TVA", desc: "Formulaire DICP 1010", icone: Percent, chemin: "/etats/declaration-tva" },
];

export default function Dashboard() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [exercices, setExercices] = useState([]);
  const [exercice, setExercice] = useState(String(anneeCourante));
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    api.get("/exercices").then(({ data }) => {
      setExercices(data || []);
      if (data && data.length) setExercice(String(data[0].annee));
    }).catch(() => {});
  }, []);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/dashboard", { params: { exercice: parseInt(exercice, 10) } });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setChargement(false);
    }
  }, [exercice]);

  useEffect(() => { charger(); }, [charger]);

  return (
    <div className="fenua-fade-up" data-testid="page-tableau-de-bord">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-[#1D3D3D]">Tableau de bord</h1>
          <p className="mt-1 text-[#1D3D3D]/70">
            Vue d'ensemble de <span className="font-semibold text-[#1D3D3D]">{company?.raison_sociale}</span>.
          </p>
        </div>
        <div className="w-44">
          <Select value={exercice} onValueChange={setExercice}>
            <SelectTrigger data-testid="filtre-exercice" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(exercices.length ? exercices : [{ id: "x", annee: anneeCourante }]).map((e) => (
                <SelectItem key={e.id} value={String(e.annee)}>Exercice {e.annee}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {chargement ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#4A8A8A]" /></div>
      ) : !data ? (
        <p className="mt-10 text-center text-sm text-[#1D3D3D]/60">Aucune donnée à afficher pour cet exercice.</p>
      ) : (
        <>
          {/* Métriques principales */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Metrique testid="metrique-tresorerie" titre="Trésorerie" valeur={fmt(data.tresorerie.total)}
              icone={Wallet} couleur="bg-[#2D5A5A]"
              detail={`Banque ${fmt(data.tresorerie.banque)} · Caisse ${fmt(data.tresorerie.caisse)}`} />
            <Metrique testid="metrique-resultat" titre="Résultat de l'exercice"
              valeur={`${data.resultat.benefice ? "+" : "−"} ${fmt(Math.abs(data.resultat.montant))}`}
              icone={data.resultat.benefice ? TrendingUp : TrendingDown}
              couleur={data.resultat.benefice ? "bg-[#1D3D3D]" : "bg-[#8A4A4A]"}
              detail={data.resultat.benefice ? "Bénéfice" : "Perte"} />
            <Metrique testid="metrique-ca" titre="Chiffre d'affaires" valeur={fmt(data.ca_exercice)}
              icone={ArrowUpRight} couleur="bg-[#4A8A8A]" detail={`Ce mois-ci : ${fmt(data.ca_mois)}`} />
            <Metrique testid="metrique-tva" titre="TVA + CPS à reverser" valeur={fmt(data.tva.somme_due)}
              icone={Percent} couleur={data.tva.somme_due > 0 ? "bg-[#8A4A4A]" : "bg-[#2D5A5A]"}
              detail={data.tva.periode_libelle} />
          </div>

          {/* Créances / dettes */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div data-testid="bloc-creances" className="bg-white border border-[#4A8A8A]/20 rounded-lg p-5 flex items-center gap-4">
              <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center"><ArrowDownLeft className="h-6 w-6 text-[#2D5A5A]" /></div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#4A8A8A]">Créances clients (à encaisser)</p>
                <p className="text-2xl font-black text-[#1D3D3D] tabular-nums">{fmt(data.creances_clients)} <span className="text-sm font-medium">XPF</span></p>
              </div>
            </div>
            <div data-testid="bloc-dettes" className="bg-white border border-[#4A8A8A]/20 rounded-lg p-5 flex items-center gap-4">
              <div className="h-11 w-11 rounded-lg bg-[#fbe3c5] flex items-center justify-center"><ArrowUpRight className="h-6 w-6 text-[#8A4A4A]" /></div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#4A8A8A]">Dettes fournisseurs (à payer)</p>
                <p className="text-2xl font-black text-[#1D3D3D] tabular-nums">{fmt(data.dettes_fournisseurs)} <span className="text-sm font-medium">XPF</span></p>
              </div>
            </div>
            <div data-testid="bloc-dettes-sociales" className="bg-white border border-[#4A8A8A]/20 rounded-lg p-5 flex items-center gap-4">
              <div className="h-11 w-11 rounded-lg bg-[#fbe3c5] flex items-center justify-center"><Users className="h-6 w-6 text-[#8A4A4A]" /></div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#4A8A8A]">Dettes sociales (CPS &amp; salaires)</p>
                <p className="text-2xl font-black text-[#1D3D3D] tabular-nums">{fmt(data.dettes_sociales)} <span className="text-sm font-medium">XPF</span></p>
              </div>
            </div>
          </div>

          {/* Mini-aperçu cliquable des états */}
          <h2 className="mt-9 text-lg font-bold text-[#1D3D3D] flex items-center gap-2"><Receipt className="h-5 w-5 text-[#4A8A8A]" /> Vos états comptables</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ETATS.map((e) => {
              const Icone = e.icone;
              return (
                <button key={e.chemin} data-testid={`apercu-${e.chemin.split("/").pop()}`} onClick={() => navigate(e.chemin)}
                  className="group text-left bg-white border border-[#4A8A8A]/20 rounded-lg p-5 hover:-translate-y-0.5 hover:border-[#4A8A8A]/50 transition-all flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-[#E8F4F4] flex items-center justify-center group-hover:bg-[#4A8A8A]/20 transition-colors"><Icone className="h-5 w-5 text-[#1D3D3D]" /></div>
                  <div className="flex-1">
                    <p className="font-bold text-[#1D3D3D]">{e.titre}</p>
                    <p className="text-xs text-[#1D3D3D]/60">{e.desc}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-[#4A8A8A] group-hover:translate-x-0.5 transition-transform" />
                </button>
              );
            })}
          </div>

          {/* Compteurs */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <Compteur testid="compteur-clients" valeur={data.compteurs.factures_clients} libelle="Factures clients" />
            <Compteur testid="compteur-fournisseurs" valeur={data.compteurs.factures_fournisseurs} libelle="Factures fournisseurs" />
            <Compteur testid="compteur-tiers" valeur={data.compteurs.tiers_actifs} libelle="Tiers actifs" />
          </div>
        </>
      )}
    </div>
  );
}

function Metrique({ titre, valeur, detail, icone: Icone, couleur, testid }) {
  return (
    <div data-testid={testid} className={`${couleur} text-white rounded-lg p-5 shadow-sm`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-white/70">{titre}</p>
        <Icone className="h-5 w-5 text-white/80" />
      </div>
      <p data-testid={`${testid}-valeur`} className="mt-3 text-2xl font-black tabular-nums">{valeur} <span className="text-sm font-medium">XPF</span></p>
      <p className="mt-1 text-xs text-white/70">{detail}</p>
    </div>
  );
}

function Compteur({ valeur, libelle, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#4A8A8A]/20 rounded-lg p-4 text-center">
      <p className="text-2xl font-black text-[#1D3D3D] tabular-nums">{valeur}</p>
      <p className="text-xs text-[#1D3D3D]/65">{libelle}</p>
    </div>
  );
}
